import * as React from 'react';
import { Spinner, BreadcrumbItem, BreadcrumbsContainer, ModalsDialog, DashboardTile } from '@smapiot/components';
import { createInstance } from 'piral-core';
import { createBreadcrumbsApi } from 'piral-breadcrumbs';
import { createDashboardApi } from 'piral-dashboard';
import { createModalsApi } from 'piral-modals';
import { createNotificationsApi } from 'piral-notifications';
import { createCloudPortalApi } from './plugins';
import { http } from './http';
import { DisplayErrorExtension, SearchExtension } from './extensions';
import { DashboardContainer, ModalsHost, Router } from './components';
import { Layout } from './layout/UserLayout';
import { routes, breadcrumbs } from './routes';
import type { GeneralData } from './types';

export function setupInstance(pages: Record<string, React.FC>, data: GeneralData) {
  const Home = React.lazy(() => import('./pages/Home'));
  const Status = React.lazy(() => import('./pages/Status'));
  const PageError = React.lazy(() => import('./pages/PageError'));
  const PageNotFound = React.lazy(() => import('./pages/PageNotFound'));
  const Notifications = React.lazy(() => import('./pages/Notifications'));
  const Settings = React.lazy(() => import('./pages/Settings'));
  const NoAccess = React.lazy(() => import('./pages/NoAccess'));

  const instance = createInstance({
    plugins: [
      createBreadcrumbsApi({ breadcrumbs }),
      createDashboardApi(),
      createModalsApi(),
      createNotificationsApi({
        defaultOptions: {
          autoClose: 0,
          type: 'info',
          unread: true,
        },
      }),
      createCloudPortalApi(data),
    ],
    state: {
      components: {
        Router,
        LoadingIndicator: Spinner,
        BreadcrumbItem: BreadcrumbItem,
        BreadcrumbsContainer,
        Layout,
        ModalsHost,
        ModalsDialog: ModalsDialog,
        DashboardContainer,
        DashboardTile: DashboardTile,
      },
      errorComponents: {
        not_found: PageNotFound,
        page: PageError,
      },
      routes: {
        ...pages,
        [routes.home]: Home,
        [routes.noauth]: NoAccess,
        [routes.notifications]: Notifications,
        [routes.settings]: Settings,
        [routes.status]: Status,
      },
    },
    requestPilets() {
      return http.getPilets();
    },
  });

  instance.root.on('api-error', () =>
    instance.root.showNotification('The HTTP request failed.', {
      title: 'API Error',
      type: 'error',
    }),
  );

  instance.root.registerExtension('display-error', DisplayErrorExtension);
  instance.root.registerExtension('search', SearchExtension);

  return instance;
}
